import React, { FC, useContext, useState } from 'react';
import { Redirect, Route, Switch as ReactRouterSwitch } from 'react-router';

import Settings from 'client/blocks/Settings/Settings';
import { DeviceLinking } from 'client/blocks-redesign/DeviceLinking/DeviceLinking';
import { GrammarPage } from 'client/blocks-redesign/grammar/GrammarPage/GrammarPage';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { useSettingsPageMenuItems } from 'client/hooks/useSettingsPageMenuItems';
import { routes } from 'client/lib/routes';
import { isInDevelopment } from 'client/model/draft';
import { AliceSkill, SmartHome } from 'client/model/skill';
import { UserAgreement } from 'client/model/userAgreement';
import { getSettingsWithUserAgreements, isSmartHomeSkill } from 'client/utils';

interface Props {
    skill: AliceSkill | SmartHome;
    isDraft: boolean;
    userAgreements: UserAgreement[];
    userAgreementsDraft: UserAgreement[];
    disabled: boolean;
}

export const AliceSkillsAppSettingsPage: FC<Props> = ({
    skill,
    isDraft,
    userAgreements,
    userAgreementsDraft,
    disabled,
}) => {
    const [hasChanges, setHasChanges] = useState(false);

    const { display } = useSettingsPageMenuItems(skill, isDraft);
    const currentAliceAppContext = useContext(CurrentAliceAppContext);

    const settings = getSettingsWithUserAgreements(skill, isDraft, isDraft ? userAgreementsDraft : userAgreements);

    return (
        <div>
            <article>
                <ReactRouterSwitch>
                    <Route path={routes.app.skillSettings(true, 'main', skill.id)}>
                        {() => (
                            <Settings
                                aliceApp={skill}
                                isDraft
                                registerChanges={setHasChanges}
                                refreshAliceApp={currentAliceAppContext?.refreshAliceApp}
                                fetchedUserAgreementsDraft={userAgreementsDraft}
                                hasChanges={hasChanges}
                                settings={settings}
                                disabled={disabled}
                            />
                        )}
                    </Route>
                    <Route path={routes.app.skillSettings(false, 'main', skill.id)}>
                        {() => (
                            <Settings
                                aliceApp={skill}
                                isDraft={false}
                                registerChanges={setHasChanges}
                                hasChanges={hasChanges}
                                settings={settings}
                                disabled={disabled}
                            />
                        )}
                    </Route>
                    {display.devices && isSmartHomeSkill(skill) && (
                        <Route path={routes.app.skillSettings(isDraft, 'devices', skill.id)}>
                            <DeviceLinking
                                isDraft={isDraft}
                                isEditable={isDraft && isInDevelopment(skill) && !disabled}
                                aliceApp={skill}
                            />
                        </Route>
                    )}
                    {display.intents && (
                        <Route path={routes.app.skillSettings(isDraft, 'intents', skill.id)}>
                            <GrammarPage aliceApp={skill} disabled={disabled} />
                        </Route>
                    )}
                    <Route path={routes.app.skillSettings(isDraft, 'userAgreements', skill.id)}>
                        <Redirect to={`${routes.app.skillSettings(isDraft, 'main', skill.id)}#user-agreements`} />
                    </Route>
                    <Redirect
                        exact
                        from={routes.app.skillPage(isDraft, 'settings', skill.id)}
                        to={routes.app.skillSettings(Boolean(isDraft), 'main', skill.id)}
                    />
                </ReactRouterSwitch>
            </article>
        </div>
    );
};
